/** Full landscape export size — PDF always uses this, even on mobile. */
export const PLEDGE_CARD_EXPORT_WIDTH = 720;
export const PLEDGE_CARD_EXPORT_HEIGHT = 400;

/** Preload remote image as data URL so export works without CORS taint. */
async function imageToDataUrl(src) {
  const response = await fetch(src, { mode: 'cors', credentials: 'omit' });
  if (!response.ok) throw new Error('Image load failed');
  const blob = await response.blob();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

function loadImageElement(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Image load failed'));
    img.src = src;
  });
}

/**
 * Crop image like CSS object-fit: cover + object-position: center top.
 * Returns a data URL sized exactly to the frame (no stretch).
 */
async function coverCropToDataUrl(imageSrc, targetWidth, targetHeight) {
  const img = await loadImageElement(imageSrc);
  const width = Math.max(1, Math.round(targetWidth));
  const height = Math.max(1, Math.round(targetHeight));

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Could not prepare card image');

  const naturalW = img.naturalWidth || img.width;
  const naturalH = img.naturalHeight || img.height;
  const imageRatio = naturalW / naturalH;
  const frameRatio = width / height;

  let cropW = naturalW;
  let cropH = naturalH;
  let cropX = 0;
  let cropY = 0;

  if (imageRatio > frameRatio) {
    cropH = naturalH;
    cropW = naturalH * frameRatio;
    cropX = (naturalW - cropW) / 2;
  } else {
    cropW = naturalW;
    cropH = naturalW / frameRatio;
    cropX = 0;
    cropY = 0;
  }

  ctx.drawImage(img, cropX, cropY, cropW, cropH, 0, 0, width, height);
  return canvas.toDataURL('image/jpeg', 0.95);
}

function resolvePhotoSource(element) {
  const frame = element.querySelector('.pledge-card-photo-frame');
  if (!frame) return null;

  const fromData = frame.getAttribute('data-photo-url');
  if (fromData) return fromData;

  const img = frame.querySelector('.pledge-card-photo-img, img');
  return img?.src || null;
}

/** Strip viewport scale transforms so html2canvas measures full 720×400 layout. */
function clearAncestorTransforms(element) {
  const restored = [];
  let node = element.parentElement;
  while (node && node !== document.body) {
    const computed = window.getComputedStyle(node);
    if (computed.transform && computed.transform !== 'none') {
      restored.push({
        node,
        transform: node.style.transform,
        transformOrigin: node.style.transformOrigin,
        width: node.style.width,
        height: node.style.height,
      });
      node.style.transform = 'none';
      node.style.transformOrigin = 'top left';
      if (node.classList.contains('pledge-card-scale-inner')) {
        node.style.width = `${PLEDGE_CARD_EXPORT_WIDTH}px`;
        node.style.height = `${PLEDGE_CARD_EXPORT_HEIGHT}px`;
      }
    }
    node = node.parentElement;
  }
  return restored;
}

function restoreAncestorTransforms(restored) {
  restored.forEach(({ node, transform, transformOrigin, width, height }) => {
    node.style.transform = transform;
    node.style.transformOrigin = transformOrigin;
    node.style.width = width;
    node.style.height = height;
  });
}

function applyExportSize(element) {
  const previous = {
    width: element.style.width,
    height: element.style.height,
    maxWidth: element.style.maxWidth,
    aspectRatio: element.style.aspectRatio,
  };
  element.style.width = `${PLEDGE_CARD_EXPORT_WIDTH}px`;
  element.style.height = `${PLEDGE_CARD_EXPORT_HEIGHT}px`;
  element.style.maxWidth = 'none';
  element.style.aspectRatio = 'auto';
  return previous;
}

function restoreExportSize(element, previous) {
  element.style.width = previous.width;
  element.style.height = previous.height;
  element.style.maxWidth = previous.maxWidth;
  element.style.aspectRatio = previous.aspectRatio;
}

/** html2canvas clone: reset transforms and force px fonts (no cqi / site heading fonts). */
function normalizeClonedCard(doc, clonedEl) {
  let parent = clonedEl.parentElement;
  while (parent) {
    parent.style.transform = 'none';
    parent.style.transformOrigin = 'top left';
    parent = parent.parentElement;
  }

  clonedEl.style.width = `${PLEDGE_CARD_EXPORT_WIDTH}px`;
  clonedEl.style.height = `${PLEDGE_CARD_EXPORT_HEIGHT}px`;
  clonedEl.style.maxWidth = 'none';
  clonedEl.style.fontFamily = "'Playfair Display', Georgia, serif";

  const serif = "'Playfair Display', Georgia, serif";
  const sans = "'Montserrat', 'Segoe UI', sans-serif";

  clonedEl.querySelectorAll(
    '.pledge-card-label, .pledge-card-couple, .pledge-card-couple-line, .pledge-card-guest, .pledge-card-amount, .pledge-card-reminder'
  ).forEach((el) => {
    el.style.fontFamily = serif;
  });

  clonedEl.querySelectorAll('.pledge-card-bank, .pledge-card-contact, .pledge-card-contact-line').forEach((el) => {
    el.style.fontFamily = sans;
  });

  clonedEl.querySelectorAll('.pledge-card-contact-line').forEach((el) => {
    el.style.display = 'block';
  });
}

async function captureCardCanvas(element) {
  if (!element) {
    throw new Error('Card is not ready yet. Please wait a moment and try again.');
  }

  const sizeRestore = applyExportSize(element);
  const transformRestore = clearAncestorTransforms(element);
  let canvas;

  try {
    await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));

    const width = PLEDGE_CARD_EXPORT_WIDTH;
    const height = PLEDGE_CARD_EXPORT_HEIGHT;
    const frame = element.querySelector('.pledge-card-photo-frame');

    let croppedPhotoDataUrl = null;
    const photoSrc = resolvePhotoSource(element);
    const frameWidth = frame?.offsetWidth || 302;
    const frameHeight = frame?.offsetHeight || height - 48;

    if (frame && photoSrc) {
      try {
        const sourceDataUrl = photoSrc.startsWith('data:')
          ? photoSrc
          : await imageToDataUrl(photoSrc);
        croppedPhotoDataUrl = await coverCropToDataUrl(
          sourceDataUrl,
          frameWidth,
          frameHeight
        );
      } catch {
        /* html2canvas may still render the img element */
      }
    }

    const { default: html2canvas } = await import('html2canvas');
    canvas = await html2canvas(element, {
      scale: 2,
      width,
      height,
      windowWidth: width,
      windowHeight: height,
      useCORS: true,
      allowTaint: false,
      backgroundColor: '#f9f9f5',
      logging: false,
      imageTimeout: 20000,
      onclone: (doc, clonedEl) => {
        normalizeClonedCard(doc, clonedEl);

        const clonedFrame = clonedEl.querySelector('.pledge-card-photo-frame');
        if (clonedFrame && croppedPhotoDataUrl && frameWidth > 0 && frameHeight > 0) {
          clonedFrame.style.backgroundImage = 'none';
          clonedFrame.innerHTML = '';
          const exportImg = doc.createElement('img');
          exportImg.src = croppedPhotoDataUrl;
          exportImg.alt = '';
          exportImg.setAttribute('aria-hidden', 'true');
          exportImg.style.display = 'block';
          exportImg.style.width = `${frameWidth}px`;
          exportImg.style.height = `${frameHeight}px`;
          exportImg.style.maxWidth = 'none';
          exportImg.style.maxHeight = 'none';
          exportImg.style.objectFit = 'cover';
          clonedFrame.style.width = `${frameWidth}px`;
          clonedFrame.style.height = `${frameHeight}px`;
          clonedFrame.style.overflow = 'hidden';
          clonedFrame.appendChild(exportImg);
        }
      },
    });
  } finally {
    restoreExportSize(element, sizeRestore);
    restoreAncestorTransforms(transformRestore);
  }

  return canvas;
}

/**
 * Capture pledge card DOM node and trigger PDF download (landscape, no image stretch).
 * @param {HTMLElement} element
 * @param {string} filename — should end with .pdf
 */
export async function downloadPledgeCardPdf(element, filename) {
  const canvas = await captureCardCanvas(element);
  const imgData = canvas.toDataURL('image/jpeg', 0.92);

  const { jsPDF } = await import('jspdf');
  const pxToMm = 0.264583;
  const pdfWidth = canvas.width * pxToMm;
  const pdfHeight = canvas.height * pxToMm;

  const pdf = new jsPDF({
    orientation: 'landscape',
    unit: 'mm',
    format: [pdfWidth, pdfHeight],
    compress: true,
  });

  pdf.addImage(imgData, 'JPEG', 0, 0, pdfWidth, pdfHeight, undefined, 'FAST');
  pdf.save(filename.endsWith('.pdf') ? filename : `${filename}.pdf`);
}

/** @deprecated Use downloadPledgeCardPdf */
export async function downloadPledgeCardPng(element, filename) {
  const canvas = await captureCardCanvas(element);
  const dataUrl = canvas.toDataURL('image/png');
  const link = document.createElement('a');
  link.download = filename;
  link.href = dataUrl;
  document.body.appendChild(link);
  link.click();
  link.remove();
}
